import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { fetchProfitAndLossActual, fetchProfitAndLossExpected, fetchExpenseDetails } from '../utils/api';
import { formatDate, formatDateTime } from '../utils/date';
import './Expenses.css';
import './Invoices.css';
import './Reports.css';
import './_Buttons.css';

const ProfitAndLossReport = ({ reportData, reportType }) => {
    const [showInvoices, setShowInvoices] = useState(false);
    const [showExpenses, setShowExpenses] = useState(false);

    const isActual = reportType === 'profit-and-loss-actual';

    return (
        <div>
            <h2>{isActual ? 'Profit and Loss (Actual)' : 'Profit and Loss (Expected)'}</h2>
            <p><strong>Income:</strong> ${(reportData.income_amount / 100).toFixed(2)}</p>
            <p><strong>Expenses:</strong> ${(reportData.expenses_amount / 100).toFixed(2)}</p>
            <p><strong>Profit: <span className={reportData.profit >= 0 ? 'profit-positive' : 'profit-negative'}>${(reportData.profit / 100).toFixed(2)}</span></strong></p>

            <div className='report-content'>
                <button onClick={() => setShowInvoices(!showInvoices)} className='cancel-button text-button margin-right-button'>
                    {showInvoices ? (isActual ? 'Hide Payments' : 'Hide Invoices') : (isActual ? 'Show Payments' : 'Show Invoices')}
                </button>
                <button onClick={() => setShowExpenses(!showExpenses)} className='cancel-button text-button'>
                    {showExpenses ? 'Hide Expenses' : 'Show Expenses'}
                </button>

                {showInvoices && (
                    <div>
                        <h3>{isActual ? 'Payments' : 'Invoices'}</h3>
                        <table className="invoices-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Student</th>
                                    <th>Name</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isActual ? (
                                    reportData.payments.map(payment => (
                                        <tr key={payment.id}>
                                            <td>{formatDate(payment.date)}</td>
                                            <td>{payment.student.first_name} {payment.student.last_name}</td>
                                            <td>{payment.invoice.name}</td>
                                            <td>${(payment.amount / 100).toFixed(2)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    reportData.invoices.map(invoice => (
                                        <tr key={invoice.id}>
                                            <td>{formatDate(invoice.due_date)}</td>
                                            <td>{invoice.student.first_name} {invoice.student.last_name}</td>
                                            <td>{invoice.name}</td>
                                            <td>${(invoice.total_amount / 100).toFixed(2)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {showExpenses && (
                    <div>
                        <h3>Expenses</h3>
                        <table className="invoices-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Category</th>
                                    <th>Name</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.expenses.map(expense => (
                                    <tr key={expense.id}>
                                        <td>{formatDate(expense.date)}</td>
                                        <td>{expense.expense_category.name}</td>
                                        <td>{expense.name}</td>
                                        <td>${(expense.amount / 100).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

const ExpenseDetailsReport = ({ reportData }) => {
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF1919'];

    const chartData = reportData.categories.filter(c => c.percent > 0).map(category => ({
        name: category.category.deleted_at
            ? `${category.category.name} (deleted on ${formatDate(category.category.deleted_at)})`
            : category.category.name,
        value: category.total,
    }));

    return (
        <div>
            <h2>Expense Details</h2>
            <div className='invoices-total-amount'>${(reportData.total / 100).toFixed(2)}</div>
            <div className="expense-details-container">
                <div className="expense-details-tables">
                    {reportData.categories.map(category => (
                        <div key={category.category.id}>
                            <h3>
                                {category.category.name}{' '}${(category.total / 100).toFixed(2)}{' '}<span className='percentage-text'>({(category.percent / 100).toFixed(2)}%)</span>{' '}
                                {category.category.deleted_at && <span className='deleted-text'>(deleted on {formatDate(category.category.deleted_at)})</span>}
                            </h3>
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {category.expenses.map(expense => (
                                        <tr key={expense.id}>
                                            <td>{expense.name}</td>
                                            <td>${(expense.amount / 100).toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
                <div className="expense-pie-chart">
                    <PieChart width={400} height={400}>
                        <Pie
                            data={chartData}
                            cx={200}
                            cy={200}
                            labelLine={false}
                            outerRadius={150}
                            fill="#8884d8"
                            dataKey="value"
                            nameKey="name"
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value) => `$${(value / 100).toFixed(2)}`} />
                        <Legend />
                    </PieChart>
                </div>
            </div>
        </div>
    );
};

const Reports = () => {
    const [reportType, setReportType] = useState('profit-and-loss-actual');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    useEffect(() => {
        const now = new Date();
        const firstDayPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDayPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    
        setFrom(firstDayPrevMonth.toISOString().split('T')[0]);
        setTo(lastDayPrevMonth.toISOString().split('T')[0]);
    }, []);

    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleFetchReport = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setReportData(null);

        try {
            let response;
            if (reportType === 'profit-and-loss-actual') {
                response = await fetchProfitAndLossActual(from, to);
            } else if (reportType === 'profit-and-loss-expected') {
                response = await fetchProfitAndLossExpected(from, to);
            } else if (reportType === 'expense-details') {
                response = await fetchExpenseDetails(from, to);
            }
            setReportData(response.data);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const renderReport = () => {
        if (loading) {
            return <div>Loading...</div>;
        }

        if (error) {
            return <div>Error: {error}</div>;
        }

        if (!reportData) {
            return null;
        }

        if (reportType === 'profit-and-loss-actual' || reportType === 'profit-and-loss-expected') {
            return <ProfitAndLossReport reportData={reportData} reportType={reportType} />;
        }

        if (reportType === 'expense-details') {
            return <ExpenseDetailsReport reportData={reportData} />;
        }

        return null;
    };

    return (
        <div className="invoices-container">
            <div className="header">
                <h1>Financial Reports</h1>
            </div>
            <form onSubmit={handleFetchReport}>
                <div className="reports-filters">
                    <div className='invoices-labels'>
                        <label>Report Type:
                            <select className="reports-select" value={reportType} onChange={(e) => { setReportType(e.target.value); setReportData(null); }}>
                                <option value="profit-and-loss-actual">Profit and Loss (Actual)</option>
                                <option value="profit-and-loss-expected">Profit and Loss (Expected)</option>
                                <option value="expense-details">Expense Details</option>
                            </select>
                        </label>
                        <div>
                            <label>From: <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required /></label>
                            <label>To: <input type="date" value={to} onChange={(e) => setTo(e.target.value)} required /></label>
                        </div>
                    </div>
                    <button type="submit" className='submit-button text-button'>Generate Report</button>
                </div>
            </form>
            <div className="report-content">
                {renderReport()}
            </div>
        </div>
    );
};

export default Reports;
